import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, } from "@mui/icons-material";
import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  nextOpenSlot,
  slotRefusal,
  toDateTimeLocal,
  fromDateTimeLocal,
} from "../../lib/slotPicker";
import type { ScheduledPost } from "../../lib/postingCalendar";

/**
 * Ch94 · PKT-0792 Phase 4 · the picker.
 *
 * Opens on the NEXT OPEN SLOT — the next future day that holds nothing, at the
 * posting hour. Not "now", which would propose a time that has already passed
 * the moment the posting hour is behind us, and not 02:41 because that happens
 * to be when he clicked.
 *
 * 🔴 IT SCHEDULES NOTHING BY ITSELF. It hands a Date to its caller on confirm.
 * And when scheduling does not exist yet — migration 106 is applied nowhere —
 * the confirm says so rather than failing on click.
 */

interface Props {
  open: boolean;
  initialWhen?: Date;
  /** What is being scheduled, so he can see it while choosing a time. */
  subject: string;
  /** Pending slots, so a taken day can be refused with a reason. */
  scheduled: ScheduledPost[];
  /** The slot being MOVED, so it does not collide with itself. */
  movingReviewId?: string;
  /** Absent ⇒ the write path does not exist; the dialog says so. */
  onConfirm?: (when: Date) => void;
  onCancel: () => void;
  now?: Date;
  /** A failure from the server, in its own words. */
  error?: string | null;
}

const ScheduleDialog: React.FC<Props> = ({
  open,
  initialWhen,
  subject,
  scheduled,
  movingReviewId,
  onConfirm,
  onCancel,
  now,
  error,
}) => {
  /*
   * 🔴 THE FIELD MUST NOT RESET WHILE HE IS TYPING — and it did.
   *
   * Found by a provider pass, NOT by my tests: `const clock = now ?? new Date()`
   * built a new Date every render, which invalidated the suggestion memo, which
   * re-ran an effect that called setValue — so every keystroke was overwritten
   * and the date was effectively UNEDITABLE. The same churn came from the
   * caller: a `scheduled = []` default parameter is a NEW ARRAY each render too.
   *
   * ⚠️ WHY MY TESTS MISSED IT: all nine injected `now`, which made the clock
   * stable and the bug unreachable. I tested the one path a user never takes.
   * `resets its suggestion only when the dialog OPENS` below runs with NO
   * injected clock, on purpose.
   *
   * The fix is not a stabler clock — it is resetting on the OPEN TRANSITION
   * only, so no amount of identity churn upstream can clobber what he typed.
   */
  const clock = React.useMemo(
    () => now ?? new Date(),
    [now, open]
  );
  const suggestion = React.useMemo(
    () => nextOpenSlot(scheduled, clock),
    [scheduled, clock]
  );
  const [value, setValue] = React.useState("");
  const wasOpen = React.useRef(false);

  React.useEffect(() => {
    // Only on the closed -> open edge. Never on a re-render, and never because
    // a parent handed us a new array literal.
    if (open && !wasOpen.current) {
      setValue(initialWhen ? toDateTimeLocal(initialWhen) : suggestion ? toDateTimeLocal(suggestion) : "");
    }
    wasOpen.current = open;
  }, [open, suggestion, initialWhen]);

  const chosen = fromDateTimeLocal(value);
  const refusal = slotRefusal(chosen, scheduled, {
    now: clock,
    ignoreSlotId: movingReviewId,
  });

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pr: 7, position: "relative" }}>When should this go out?<span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={onCancel}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span></DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" data-testid="schedule-subject">
            {subject}
          </Typography>

          <TextField
            type="datetime-local"
            fullWidth
            value={value}
            onChange={(event) => setValue(event.target.value)}
            InputLabelProps={{ shrink: true }}
            label="Date and time"
            data-testid="schedule-when"
          />

          {suggestion ? (
            <Typography variant="caption" color="text.secondary" data-testid="schedule-hint">
              Next open slot is{" "}
              {suggestion.toLocaleDateString(undefined, {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}{" "}
              at{" "}
              {suggestion.toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
              })}
              .
            </Typography>
          ) : (
            // Rather than proposing a date nobody chose, two months out.
            <Typography variant="caption" color="text.secondary" data-testid="schedule-no-slot">
              Every day for the next couple of months already has something going out.
              Pick a date yourself.
            </Typography>
          )}

          {refusal ? (
            <Alert severity="warning" data-testid="schedule-refusal">
              {refusal}
            </Alert>
          ) : null}

          {error ? (
            <Alert severity="error" data-testid="schedule-error">
              {error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} data-testid="schedule-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={Boolean(refusal) || !onConfirm || !chosen}
          onClick={() => chosen && onConfirm?.(chosen)}
          data-testid="schedule-confirm"
        >
          {onConfirm ? "Schedule it" : "Scheduling isn't built yet"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog;
